/**
 * @fileoverview Timesheet calendar view — 7-day time grid (MOR-906 §7.1)
 *
 * A Google-Calendar-style week grid for time entries, built on the app
 * calendar's drag-to-select hook with snapping set to the firm's billing
 * increment (6-minute units by default):
 *
 * - Entries with a `startTime` render as solid blocks at their actual time.
 * - Entries without one render as dashed "estimated placement" blocks packed
 *   sequentially from 08:00 — the calendar stays useful before start times
 *   (or external calendar sync, a V2 connector) exist. Units remain the
 *   duration source of truth everywhere.
 * - Dragging an empty range opens the Log time dialog with date,
 *   startTime and units (duration ÷ increment) pre-filled, without
 *   leaving the calendar.
 * - Clicking a block opens the entry detail sheet (history, notes, status).
 */

import { useMemo } from "react";
import { format, parseISO } from "date-fns";

import type { Id } from "@cvx/_generated/dataModel";
import type { TimeRoundingMode } from "@cvx/timesheets/timesheetSettings/schema";
import type { TaskColor } from "@cvx/lib/taskColor.schema";
import { minutesToUnits } from "@cvx/timesheets/units";
import { cn } from "@/utils/misc";
import { TASK_COLOR_DOT } from "@/components/settings/tasks/task-colors";
import { useTimeGridSelection } from "@/calendar/components/week-and-day-view/use-time-grid-selection";

const FIRST_HOUR = 7;
const TOTAL_HOURS = 13; // 07:00 – 20:00 visible
const HOUR_HEIGHT_PX = 64;
/** Untimed entries pack downward from here. */
const PACK_START_MIN = 8 * 60;
/** A block is never drawn shorter than this, or its text would be unreadable. */
const MIN_BLOCK_PX = 18;
/** So packing has to step by at least the drawn height, otherwise a run of
 *  short entries (a 6-minute unit is 6px tall) draws on top of itself. */
const MIN_BLOCK_MIN = (MIN_BLOCK_PX / HOUR_HEIGHT_PX) * 60;

type EntryStatus = "draft" | "submitted" | "approved" | "rejected";

interface CalendarEntry {
  _id: Id<"timeEntries">;
  date: string;
  startTime?: string;
  jobName: string;
  categoryId: Id<"timeCategories">;
  categoryName: string;
  narrative: string;
  units: number;
  status: EntryStatus;
}

/** Mini status tag inside a block — same palette as the list badges. */
const STATUS_TAG: Record<EntryStatus, { label: string; className: string }> = {
  draft: { label: "Draft", className: "bg-muted text-muted-foreground" },
  submitted: {
    label: "Submitted",
    className:
      "bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-300",
  },
  approved: {
    label: "Approved",
    className:
      "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300",
  },
  rejected: {
    label: "Rejected",
    className: "bg-red-100 text-red-800 dark:bg-red-950/60 dark:text-red-300",
  },
};

const STATUS_BLOCK: Record<EntryStatus, string> = {
  draft: "bg-primary/10 border-primary/40 text-foreground hover:bg-primary/15",
  submitted:
    "bg-blue-100/80 border-blue-300 text-blue-900 hover:bg-blue-100 dark:bg-blue-950/50 dark:border-blue-800 dark:text-blue-200",
  approved:
    "bg-emerald-100/80 border-emerald-300 text-emerald-900 hover:bg-emerald-100 dark:bg-emerald-950/50 dark:border-emerald-800 dark:text-emerald-200",
  rejected:
    "bg-red-100/80 border-red-300 text-red-900 hover:bg-red-100 dark:bg-red-950/50 dark:border-red-800 dark:text-red-200",
};

function hhMmToMin(time: string): number {
  const [h, m] = time.split(":").map(Number);
  return h * 60 + m;
}

function minToHhMm(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

interface PlacedBlock {
  entry: CalendarEntry;
  startMin: number;
  durationMin: number;
  estimated: boolean;
}

export function TimesheetCalendarView({
  weekDays,
  entries,
  unitMinutes,
  roundingMode,
  todayIso,
  categoryVisual,
  onSlotSelect,
  onEntryClick,
}: {
  weekDays: string[];
  entries: CalendarEntry[];
  unitMinutes: number;
  /** MOR-1016 — firm's part-unit rounding direction for the drag-to-select. */
  roundingMode: TimeRoundingMode;
  todayIso: string;
  categoryVisual: (categoryId: Id<"timeCategories">) => {
    color?: TaskColor;
    icon?: string;
  };
  onSlotSelect: (args: {
    date: string;
    startTime: string;
    units: number;
  }) => void;
  onEntryClick: (entryId: Id<"timeEntries">) => void;
}) {
  const { selection, onColumnPointerDown, minToPx } = useTimeGridSelection({
    firstHour: FIRST_HOUR,
    totalHours: TOTAL_HOURS,
    hourHeightPx: HOUR_HEIGHT_PX,
    snapMinutes: unitMinutes,
    defaultDurationMin: unitMinutes * 5,
    onSelect: (dayKey, startMin, endMin) => {
      const durationMin = Math.max(unitMinutes, endMin - startMin);
      onSlotSelect({
        date: dayKey,
        startTime: minToHhMm(startMin),
        units: minutesToUnits(durationMin, unitMinutes, roundingMode),
      });
    },
  });

  // Place entries: timed blocks at their startTime, untimed packed from
  // 08:00 (after any timed blocks' latest end, so they don't sit under
  // real appointments when both exist). Everything is clamped into the
  // visible window so a 6am or 9pm entry renders at the edge instead of
  // overflowing the grid.
  const visibleStartMin = FIRST_HOUR * 60;
  const visibleEndMin = (FIRST_HOUR + TOTAL_HOURS) * 60;
  const blocksByDay = useMemo(() => {
    const clamp = (startMin: number, durationMin: number) => {
      const start = Math.min(
        Math.max(startMin, visibleStartMin),
        visibleEndMin - unitMinutes,
      );
      const end = Math.min(start + durationMin, visibleEndMin);
      return {
        startMin: start,
        durationMin: Math.max(end - start, unitMinutes),
      };
    };

    const map = new Map<string, PlacedBlock[]>();
    for (const day of weekDays) {
      const dayEntries = entries.filter((e) => e.date === day);
      const blocks: PlacedBlock[] = [];
      const latestTimedEnd = dayEntries.reduce((latest, e) => {
        if (!e.startTime) return latest;
        const placed = clamp(hhMmToMin(e.startTime), e.units * unitMinutes);
        blocks.push({ entry: e, ...placed, estimated: false });
        // Step past the block as DRAWN: a 6-minute block is padded to the
        // minimum height, and packing from its nominal end put the first
        // untimed block on top of it (QA-51).
        return Math.max(
          latest,
          placed.startMin + Math.max(placed.durationMin, MIN_BLOCK_MIN),
        );
      }, PACK_START_MIN);
      const untimedStart = blocks.length > 0 ? latestTimedEnd : PACK_START_MIN;
      dayEntries.reduce((cursor, e) => {
        if (e.startTime) return cursor;
        const placed = clamp(cursor, e.units * unitMinutes);
        blocks.push({ entry: e, ...placed, estimated: true });
        return placed.startMin + Math.max(placed.durationMin, MIN_BLOCK_MIN);
      }, untimedStart);
      map.set(day, blocks);
    }
    return map;
  }, [entries, weekDays, unitMinutes, visibleStartMin, visibleEndMin]);

  const hours = Array.from({ length: TOTAL_HOURS }, (_, i) => FIRST_HOUR + i);

  // Driven by the day count rather than fixed at seven, so the day view fills
  // the card instead of leaving six empty columns beside a narrow strip.
  const columns = {
    gridTemplateColumns: `3.5rem repeat(${Math.max(1, weekDays.length)}, minmax(0, 1fr))`,
  };

  return (
    <div className="overflow-hidden rounded-xl bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)]">
      {/* Day headers */}
      <div className="grid border-b border-border/60" style={columns}>
        <div />
        {weekDays.map((day) => (
          <div
            key={day}
            className={cn(
              "border-l border-border/40 px-2 py-2 text-center text-xs font-medium",
              day === todayIso ? "text-primary" : "text-muted-foreground",
            )}
          >
            {format(parseISO(day), "EEE d")}
          </div>
        ))}
      </div>

      {/* Time grid */}
      <div className="max-h-[calc(100vh-320px)] overflow-y-auto">
        <div className="grid" style={columns}>
          {/* Hour gutter */}
          <div
            className="relative"
            style={{ height: TOTAL_HOURS * HOUR_HEIGHT_PX }}
          >
            {hours.map((h) => (
              <div
                key={h}
                className="absolute right-2 -translate-y-1/2 text-[10px] tabular-nums text-muted-foreground"
                style={{ top: (h - FIRST_HOUR) * HOUR_HEIGHT_PX }}
              >
                {h === 0
                  ? ""
                  : format(new Date(2000, 0, 1, h), "ha").toLowerCase()}
              </div>
            ))}
          </div>

          {/* Day columns */}
          {weekDays.map((day) => {
            const blocks = blocksByDay.get(day) ?? [];
            const isSelecting = selection?.dayKey === day;
            return (
              <div
                key={day}
                className="relative cursor-crosshair select-none border-l border-border/40"
                style={{ height: TOTAL_HOURS * HOUR_HEIGHT_PX }}
                onMouseDown={(e) => onColumnPointerDown(e, day)}
              >
                {/* Hour lines */}
                {hours.map((h) => (
                  <div
                    key={h}
                    className="absolute inset-x-0 border-t border-border/30"
                    style={{ top: (h - FIRST_HOUR) * HOUR_HEIGHT_PX }}
                  />
                ))}

                {/* Live drag selection */}
                {isSelecting && selection && (
                  <div
                    className="absolute inset-x-0.5 z-20 rounded border border-primary/60 bg-primary/15"
                    style={{
                      top: minToPx(selection.startMin),
                      height: Math.max(
                        8,
                        minToPx(selection.endMin) - minToPx(selection.startMin),
                      ),
                    }}
                  >
                    <span className="px-1 text-[10px] font-medium text-primary">
                      {minToHhMm(selection.startMin)}–
                      {minToHhMm(selection.endMin)}
                    </span>
                  </div>
                )}

                {/* Entry blocks */}
                {blocks.map(({ entry, startMin, durationMin, estimated }) => {
                  const top = minToPx(startMin);
                  const height = Math.max(
                    MIN_BLOCK_PX,
                    (durationMin / 60) * HOUR_HEIGHT_PX,
                  );
                  const visual = categoryVisual(entry.categoryId);
                  const statusTag = STATUS_TAG[entry.status];
                  return (
                    <button
                      key={entry._id}
                      type="button"
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={() => onEntryClick(entry._id)}
                      title={`${entry.jobName} · ${entry.categoryName} · ${statusTag.label} — ${entry.narrative}${
                        estimated
                          ? " (estimated placement — no start time)"
                          : ""
                      }`}
                      className={cn(
                        "absolute inset-x-0.5 z-10 overflow-hidden rounded border px-1 py-0.5 text-left text-[10px] leading-tight transition-colors",
                        STATUS_BLOCK[entry.status],
                        estimated && "border-dashed opacity-80",
                      )}
                      style={{ top, height }}
                    >
                      <span className="block truncate font-medium">
                        {estimated ? "≈ " : ""}
                        {entry.jobName}
                      </span>
                      {height >= 36 && (
                        <span className="mt-0.5 flex flex-wrap items-center gap-1">
                          <span className="inline-flex max-w-full items-center gap-1 truncate rounded-sm bg-background/80 px-1 py-px text-[9px] font-medium text-foreground">
                            <span
                              className={cn(
                                "size-1.5 shrink-0 rounded-full",
                                TASK_COLOR_DOT[visual.color ?? "gray"],
                              )}
                            />
                            <span className="truncate">
                              {entry.categoryName}
                            </span>
                          </span>
                          <span
                            className={cn(
                              "inline-flex shrink-0 items-center rounded-sm px-1 py-px text-[9px] font-medium",
                              statusTag.className,
                            )}
                          >
                            {statusTag.label}
                          </span>
                        </span>
                      )}
                      {height >= 60 && (
                        <span className="mt-0.5 block truncate opacity-75">
                          {entry.narrative}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>

      <div className="border-t border-border/40 px-3 py-1.5 text-[11px] text-muted-foreground">
        Drag an empty slot to add an entry (snaps to {unitMinutes}-minute
        units). Dashed blocks are entries without a start time, packed as an
        estimate. Click a block to open the entry.
      </div>
    </div>
  );
}
